import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { switchMap, take } from 'rxjs';
import { AUTH_PROVIDER } from '../auth/auth-provider.interface';
import { isCockpitApiRequest } from '../auth/api-origin.utils';

/**
 * Attaches the signed-in user's access token to Cockpit API requests.
 *
 * Two deliberate properties:
 * - Requests are selected by trusted destination (the configured API origin and its allowed
 *   paths), so a token is never sent to an unrelated host.
 * - A failed token acquisition aborts the request instead of retrying it without credentials.
 *   Errors coming back from the server are left untouched, so an authentication problem is never
 *   confused with an API response.
 */
export const authenticationInterceptor: HttpInterceptorFn = (req, next) => {
  const authProvider = inject(AUTH_PROVIDER);

  if (!isCockpitApiRequest(req.url)) {
    return next(req);
  }

  return authProvider.accessToken$(req.url).pipe(
    take(1),
    switchMap((accessToken) => {
      if (!accessToken) {
        // No provider token available, for example under the demo credentials provider. The
        // request proceeds unchanged and the API decides whether it is acceptable.
        return next(req);
      }

      const authenticatedRequest = req.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return next(authenticatedRequest);
    })
  );
};
